import { Palmtree, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";

import { useTeamTimesheet } from "@/api/queries";
import { useCurrentUser } from "@/app/auth";
import { EmptyState } from "@/components/common/EmptyState";
import { PageHeader } from "@/components/common/PageHeader";
import { UserAvatar } from "@/components/common/UserAvatar";
import { VacationBadge } from "@/components/common/VacationBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { TeamSelect } from "@/features/activity/TeamSelect";
import { dateInZone, formatDuration, formatWeekday, shiftDate, weekStartOf } from "@/lib/format";
import { cn } from "@/lib/utils";

import { TimesheetTabs } from "./TimesheetTabs";
import { WeekPicker } from "./WeekPicker";

/** People × days of a week for managers and administrators. */
export function TeamTimesheetPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "ru";
  const me = useCurrentUser();
  const [params, setParams] = useSearchParams();
  const today = dateInZone(new Date(), me.timezone);
  const currentWeek = weekStartOf(today);
  const week = params.get("week") ? weekStartOf(params.get("week") ?? "") : currentWeek;
  const teamId = params.get("team") ?? undefined;
  const sheet = useTeamTimesheet(teamId, week, shiftDate(week, 6));
  const data = sheet.data;

  const update = (next: { week?: string; team?: string | null }) => {
    const p = new URLSearchParams(params);
    if (next.week !== undefined) {
      if (next.week === currentWeek) p.delete("week");
      else p.set("week", next.week);
    }
    if (next.team !== undefined) {
      if (next.team) p.set("team", next.team);
      else p.delete("team");
    }
    setParams(p, { replace: true });
  };
  const weekParam = week === currentWeek ? "" : `&week=${week}`;
  const dayTotals = data?.dates.map((_, i) =>
    data.rows.reduce((s, r) => s + (r.tracked_seconds[i] ?? 0) + (r.manual_seconds[i] ?? 0), 0),
  );

  return (
    <>
      <PageHeader
        title={t("nav.timesheets")}
        actions={
          <>
            <TeamSelect value={teamId} onChange={(team) => update({ team: team ?? null })} />
            <WeekPicker week={week} currentWeek={currentWeek} onChange={(w) => update({ week: w })} />
          </>
        }
      />
      <TimesheetTabs />
      {sheet.isError ? (
        <Card>
          <EmptyState
            icon={Users}
            title={t("common.error")}
            action={
              <Button variant="secondary" onClick={() => void sheet.refetch()}>
                {t("common.retry")}
              </Button>
            }
          />
        </Card>
      ) : !data ? (
        <Skeleton className="h-80 w-full" />
      ) : data.rows.length === 0 ? (
        <Card>
          <EmptyState icon={Users} title={t("timesheets.teamEmpty")} />
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <p className="px-5 pt-4 pb-3 text-muted">{t("timesheets.teamHint")}</p>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-left">
              <thead className="bg-surface-muted text-xs text-muted">
                <tr>
                  <th className="px-5 py-2.5 font-medium">{t("timesheets.columns.employee")}</th>
                  {data.dates.map((d) => (
                    <th
                      key={d}
                      className={cn("px-3 py-2.5 text-right font-medium", d === today && "text-primary")}
                    >
                      {formatWeekday(d, lang)}
                    </th>
                  ))}
                  <th className="px-5 py-2.5 text-right font-medium">{t("timesheets.columns.total")}</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={row.user.id} className="border-t">
                    <td className="px-5 py-2.5">
                      <Link
                        to={`/timesheets?user=${row.user.id}${weekParam}`}
                        className="flex items-center gap-2.5 font-medium hover:underline"
                      >
                        <UserAvatar name={row.user.full_name} className="size-7" />
                        <span className="truncate">{row.user.full_name}</span>
                        <VacationBadge vacation={row.user.vacation} />
                      </Link>
                    </td>
                    {data.dates.map((d, i) => {
                      const tracked = row.tracked_seconds[i] ?? 0;
                      const manual = row.manual_seconds[i] ?? 0;
                      return (
                        <td
                          key={d}
                          className={cn(
                            "px-3 py-2.5 text-right tabular-nums",
                            d === today && "bg-primary-soft/40",
                          )}
                        >
                          <span className="inline-flex items-center justify-end gap-1">
                            {row.vacation[i] && (
                              <Palmtree
                                className="size-3.5 text-primary"
                                aria-label={t("timesheets.legendVacation")}
                              />
                            )}
                            {tracked + manual > 0 ? (
                              <span
                                className={cn(manual > 0 && "underline decoration-dotted underline-offset-4")}
                                title={
                                  manual > 0
                                    ? t("timesheets.manualShort", { time: formatDuration(manual, lang) })
                                    : undefined
                                }
                              >
                                {formatDuration(tracked + manual, lang)}
                              </span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </span>
                        </td>
                      );
                    })}
                    <td className="px-5 py-2.5 text-right font-semibold tabular-nums">
                      {row.total_seconds > 0 ? formatDuration(row.total_seconds, lang) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t bg-surface-muted/60 font-semibold">
                <tr>
                  <td className="px-5 py-2.5">{t("timesheets.columns.total")}</td>
                  {data.dates.map((d, i) => (
                    <td key={d} className="px-3 py-2.5 text-right tabular-nums">
                      {dayTotals?.[i] ? formatDuration(dayTotals[i], lang) : "—"}
                    </td>
                  ))}
                  <td className="px-5 py-2.5 text-right tabular-nums">
                    {formatDuration(
                      data.rows.reduce((s, r) => s + r.total_seconds, 0),
                      lang,
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}
